import { Route } from "~/enums/route";
import { RouteHelper } from "~/helpers/routeHelper";
import { Constructor } from "../../../global/constructor";
import { Hash } from "../../interfaces/hashInterfaces";
import IProvider, { IProviderRoutes } from "../../interfaces/provider";
import { IResponse, IServerData } from "../../interfaces/serverInterfaces";
import Model from "../../models/model";
import Service from "../../services/service";
import { HTTPMethod } from "./httpMethod";
import jqXHR = JQuery.jqXHR;
import serverAction from "~/serverAction"

export default class Provider<T extends Model, R extends IResponse = IResponse, RS extends IResponse = IResponse> extends Service implements IProvider<T> {
  protected routes: IProviderRoutes;
  constructor(protected modelType: Constructor<T>, public modelRoot: string, public collectionRoot: string, protected endPointBase: string = collectionRoot) {
    super();

    this.routes = {
      fetchOne: `${this.endPointBase}/:id`,
      fetchAll: `${this.endPointBase}`,
      update: `${this.endPointBase}/:id`,
      create: `${this.endPointBase}`
    };
  }


  fetchOne(id: string, opts: object = {}): Promise<IServerData<R>> {
    return new Promise<IServerData<R>>((resolve, reject) => {
      this.serverAction<R>({
        endpoint: this.replace(this.routes.fetchOne, {...opts, id: id}),
        method: HTTPMethod.GET,
        json: opts,
      }).then(resolve).catch(reject);
    });
  }

  fetchAll(opts: object = {}): Promise<IServerData<RS>> {
    return new Promise<IServerData<RS>>((resolve, reject) => {
      this.serverAction<RS>({
        endpoint:this.replace(this.routes.fetchAll, {}, opts),
        method: HTTPMethod.GET
      }).then(resolve).catch(reject);
    });
  }

  save(obj: T, objJson?: object, fromUrl?: string ): Promise<IServerData<R>> {
    let endpoint: string;
    let method: HTTPMethod;
    if (obj.id) {
      endpoint = this.replace(this.routes.update, obj);
      method = HTTPMethod.PUT;
    } else {
      endpoint = this.replace(this.routes.create, obj);
      method = HTTPMethod.POST;
    }
    return new Promise<IServerData<R>>((resolve, reject) => {
      this.serverAction<R>({
        endpoint: endpoint,
        method: method,
        json: {[this.modelRoot]: objJson || obj.asJson({includeDeletions: true}), fromUrl: fromUrl}
      }).then(resolve).catch(reject);
    });
  }

  // can't be called delete
  destroy(obj: T): Promise<IServerData<R>> {
    const endpoint = this.replace(this.routes.update, obj);
    const method = HTTPMethod.DELETE;

    return new Promise<IServerData<R>>((resolve, reject) => {
      this.serverAction<R>({
        endpoint: endpoint,
        method: method,
      }).then(resolve).catch(reject);
    });
  }

  createOrUpdateFrom(obj: T, objJson: {client_id?, id?}): T {
    const clientIdOrId = objJson.client_id || objJson.id;
    const repo = (<any>this.modelType).repo;
    const o = obj || (repo && repo.find(clientIdOrId)) || new this.modelType();
    o.loadFromJson(objJson);
    return o;
  }

  createOrUpdateManyFrom(objsJson: {client_id?, id?}[]): T[] {
    return objsJson.map(json => this.createOrUpdateFrom(null, json));
  }

  pathFor(route: Route, model: T): string {
    return RouteHelper.path({route: route, modelOrId: model} )
  }

  replacer(route: string, obj: object, metadata: object = {}): string {
    return route;
  }

  protected replace(route: string, obj: Hash = {}, metadata: Hash = {}): string {
    return this.replacer(this.baseReplace(route, obj), obj, metadata);
  }

  private baseReplace(route: string, obj: Hash = {}): string {
    return route.replace(':id', obj.id);
  }

  protected serverAction<U = R | RS>({endpoint = '', method = 'get', json = null}): Promise<IServerData<U>> {
    // logic extracted to own function to allow usage without needing all Provider requirements (like a model class)
    return serverAction<U>({ endpoint, method, json })
  }
}
